import React from 'react';
import { Field, Form, Formik } from 'formik';
import { SubmitButtonV3 } from 'Components/buttons/SubmitButton/SubmitButtonV3';
import { Flex, SimpleGrid, Box, useSnackbar } from 'pounceV2';
import { FormikTextInputV3 } from 'Components/fields/TextInput/TextInputV3';
import * as Yup from 'yup';
import { isSamlUser } from 'Helpers/utils';
import FormikComboboxV3 from 'Components/fields/ComboBox/ComboBoxV3';
import { useListUsersRolesSaml } from './graphql/listUsersRolesSaml.generated';

export interface UserFormValues {
  id?: string; // optional value
  email: string;
  familyName: string;
  givenName: string;
  role?: {
    id: string | null;
    name: string;
  };
}
export interface UserFormProps {
  /** The initial values of the form */
  initialValues: UserFormValues;
  /** callback for the submission of the form */
  onSubmit: (values: UserFormValues) => void;
}

const UserFormV3: React.FC<UserFormProps> = ({ initialValues, onSubmit }) => {
  const { pushSnackbar } = useSnackbar();

  const { data } = useListUsersRolesSaml({
    onError: () => pushSnackbar({ variant: 'error', title: "Couldn't fetch needed data" }),
  });

  const samlRoleName = data?.roles.find(({ id }) => id === data.samlConfig?.defaultRoleId)?.name;

  // SAML role doesn't have a specific id
  const samlRole = React.useMemo(
    () => ({ id: '', name: samlRoleName ? `${samlRoleName} (SAML Default)` : 'SAML Default' }),
    [samlRoleName],
  );

  // When we edit a user we want to exclude its email from the validation schema
  // When we create a user, initialValues.id is undefined and no user is filtered
  // We shall also remove any emails related with SAML as users with SAML email can be twice in the list
  const existingUsers =
    data?.users.filter(user => user.id !== initialValues.id && !isSamlUser(user)) ?? [];

  const usersEmails = existingUsers.map(u => u.email);

  const isComingFromSaml = isSamlUser(initialValues);

  const validationSchema = Yup.object().shape({
    familyName: isComingFromSaml ? Yup.string() : Yup.string().required('Last name is required'),
    givenName: isComingFromSaml ? Yup.string() : Yup.string().required('First name is required'),
    role: Yup.object().shape({
      id: isComingFromSaml ? Yup.string().nullable() : Yup.string().required(),
      name: Yup.string().required(),
    }),
    email: isComingFromSaml
      ? Yup.string().email('Must be a valid email') // For SAML users we don't need to validate the email since users can't edit them
      : Yup.string()
          .email('Must be a valid email')
          .notOneOf(usersEmails, 'Email already in use')
          .required('Email is required'),
  });

  const availableRoles = React.useMemo(() => {
    const roles = data?.roles ?? [];
    // Add SAML option for SAML related users.
    // An empty related string identifies SAML user's roleId
    return isComingFromSaml
      ? [...roles, samlRole].sort((prev, curr) => {
          return prev.name.localeCompare(curr.name);
        })
      : roles;
  }, [data, isComingFromSaml, samlRole]);

  return (
    <Formik<UserFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      <Form data-tid="user-form">
        <Flex direction="column" gap={5}>
          <Field
            data-tid="user-form-email"
            as={FormikTextInputV3}
            label="Email address"
            placeholder="john@doe.com"
            disabled={isComingFromSaml}
            name="email"
            required
          />
          <SimpleGrid columns={2} gap={5}>
            <Field
              data-tid="user-form-first-name"
              as={FormikTextInputV3}
              label="First Name"
              disabled={isComingFromSaml}
              placeholder="John"
              name="givenName"
              required
            />
            <Field
              data-tid="user-form-last-name"
              as={FormikTextInputV3}
              label="Last Name"
              disabled={isComingFromSaml}
              placeholder="Doe"
              name="familyName"
              required
            />
          </SimpleGrid>
          <Box as="fieldset">
            <Field
              data-tid="user-form-role"
              name="role"
              as={FormikComboboxV3}
              searchable
              label="Role"
              itemToString={role => role.name}
              items={availableRoles || []}
              placeholder="Select a role for this user..."
              aria-describedby="role-helper-text"
              showClearSelectionControl={false}
            />
          </Box>
          <SubmitButtonV3
            size="xlarge"
            fullWidth
            data-tid={`${initialValues.id ? 'updated' : 'invited'}-user`}
          >
            {initialValues.id ? 'Update' : 'Invite'}
          </SubmitButtonV3>
        </Flex>
      </Form>
    </Formik>
  );
};

export default UserFormV3;
